// ─── Unattended changelog publishing ────────────────────────────────
// Whether a deploy's notes may go to Slack without a human clicking Approve.
//
// The rule lives here rather than in either service because both need it and
// they may not import each other: `services/users` applies it the moment notes
// are written, and `services/api` re-applies it on sync to catch the ones that
// were held back by a grace period or by quiet hours. Two copies of this
// decision would eventually disagree, and the failure mode is an announcement
// nobody approved.

/**
 * Who an unattended approval is credited to. Not a real user id — no account
 * pressed the button — and denormalized onto the row rather than derived, so a
 * card can still say how the notes went out long after the setting that did it
 * was changed back.
 */
export const CHANGELOG_AUTO_PUBLISH_ACTOR = {
    id: 'system:changelog-auto-publish',
    name: 'Nexus',
} as const;

/** The only `NEXUS_ENVIRONMENT` whose automation may post, mail, or announce unattended. */
const CHANGELOG_AUTOMATION_ENVIRONMENT = 'production';

/**
 * Whether this deployment may run the changelog automation at all.
 *
 * Every deployment reads the same GitHub Releases, and a PR env built with
 * `sync-production-data` also inherits production's settings. Without this
 * gate each such env treated the day's releases as new, wrote its own notes
 * and posted them to the team channel — 8 duplicate posts on 2026-11-02 from
 * three PR envs. Manual Post and Send are untouched so a PR env can still
 * exercise delivery against a scratch webhook.
 */
export function isChangelogAutomationEnvironment(environment: string): boolean {
    return environment === CHANGELOG_AUTOMATION_ENVIRONMENT;
}

/** The settings this decision reads. A structural subset of `ChangelogSettings`. */
export interface ChangelogAutoPublishSettings {
    requireDeployApproval: boolean;
    autoPublishDelayMinutes: number;
    quietHoursEnabled: boolean;
    quietHoursStartMinute: number;
    quietHoursEndMinute: number;
    timezone: string;
    minItemsToAutoPublish: number;
}

export interface ChangelogAutoPublishCandidate {
    /** When the notes became ready — where the grace period is measured from. */
    readyAt: Date;
    /** User-facing changes the generator kept. Zero means an internal-only deploy. */
    itemCount: number;
    /** A human has already signed off, so the send is theirs to trigger. */
    isApproved: boolean;
}

/**
 * Why an entry was not posted. Returned rather than logged so the caller can
 * tell "not yet" from "not ever" — the first is worth looking at again on the
 * next sweep, the second is not.
 */
export type ChangelogAutoPublishBlocker =
    | 'approval-required'
    | 'already-approved'
    | 'nothing-to-say'
    | 'waiting-out-delay'
    | 'quiet-hours';

export type ChangelogAutoPublishDecision =
    | { publish: true }
    | { publish: false; reason: ChangelogAutoPublishBlocker };

const MINUTES_IN_DAY = 24 * 60;
const MS_PER_MINUTE = 60 * 1000;

/**
 * Minutes past local midnight in `timezone`.
 *
 * Formatted rather than offset-arithmetic so daylight saving is the platform's
 * problem: a fixed offset would put the quiet window an hour out for half the
 * year in every zone that shifts. An unknown zone throws, so it is treated as
 * UTC — a quiet window in the wrong zone beats a worker that cannot publish.
 */
export function minuteOfDayIn(at: Date, timezone: string): number {
    const parts = quietHoursFormatter(timezone).formatToParts(at);
    const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
    const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
    // `en-GB` with hour12 false renders local midnight as 24, not 00, in some
    // runtimes. Folding it back keeps the value in range for the comparison.
    return (hour % 24) * 60 + minute;
}

function quietHoursFormatter(timezone: string): Intl.DateTimeFormat {
    const options: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    };
    try {
        return new Intl.DateTimeFormat('en-GB', { ...options, timeZone: timezone });
    } catch {
        return new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' });
    }
}

/**
 * Whether `minute` falls inside the window. A start later than the end is the
 * ordinary overnight case — 22:00 to 08:00 — and wraps past midnight.
 */
export function isWithinQuietHours(
    minute: number,
    startMinute: number,
    endMinute: number,
): boolean {
    const start = ((startMinute % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
    const end = ((endMinute % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
    if (start === end) return false;
    return start < end ? minute >= start && minute < end : minute >= start || minute < end;
}

/**
 * Whether these notes may be approved and posted with nobody watching.
 *
 * Order matters: the permanent refusals are checked before the temporary ones,
 * so an entry that will never auto-post is not reported every sweep as though
 * it were about to.
 */
export function decideAutoPublish(
    settings: ChangelogAutoPublishSettings,
    candidate: ChangelogAutoPublishCandidate,
    now: Date,
): ChangelogAutoPublishDecision {
    if (settings.requireDeployApproval) {
        return { publish: false, reason: 'approval-required' };
    }
    if (candidate.isApproved) {
        return { publish: false, reason: 'already-approved' };
    }
    // A deploy of nothing but internal work produces a short list or an empty
    // one, and announcing it spends the channel's attention to say nothing.
    // The floor is 1, so an empty entry stops here whatever the setting is.
    if (candidate.itemCount < settings.minItemsToAutoPublish) {
        return { publish: false, reason: 'nothing-to-say' };
    }

    const dueAt = candidate.readyAt.getTime() + settings.autoPublishDelayMinutes * MS_PER_MINUTE;
    if (now.getTime() < dueAt) {
        return { publish: false, reason: 'waiting-out-delay' };
    }

    if (
        settings.quietHoursEnabled &&
        isWithinQuietHours(
            minuteOfDayIn(now, settings.timezone),
            settings.quietHoursStartMinute,
            settings.quietHoursEndMinute,
        )
    ) {
        return { publish: false, reason: 'quiet-hours' };
    }

    return { publish: true };
}

// ─── What users see ─────────────────────────────────────────────────
// In-app announcements are gated separately from internal automation, so
// making the pipeline more automatic can never be the thing that starts
// interrupting every user. HOLD is the shipped default per product direction:
// nothing user-facing goes out, however automatic Slack and email become.

/** Mirrors the `ChangelogUserFacingAnnouncements` DB enum. */
export type ChangelogUserFacingGate = 'HOLD' | 'BIG_NEWS_ONLY' | 'EVERYTHING';

/** Mirrors the `ChangelogSignificance` DB enum. */
export type ChangelogEntrySignificance = 'BIG_NEWS' | 'SMALL_UPDATE';

/**
 * Whether one deploy's notes may be announced inside the app. Small updates
 * never qualify under BIG_NEWS_ONLY — they wait for the roundup, which is the
 * artifact that keeps frequent small deploys from reading as spam.
 */
export function mayAnnounceEntryInApp(
    gate: ChangelogUserFacingGate,
    significance: ChangelogEntrySignificance,
): boolean {
    if (gate === 'HOLD') return false;
    if (gate === 'EVERYTHING') return true;
    return significance === 'BIG_NEWS';
}

/**
 * Whether a roundup may be announced inside the app. A roundup is already the
 * curated, batched form — the thing small updates wait for — so any gate other
 * than HOLD lets it through.
 */
export function mayAnnounceDigestInApp(gate: ChangelogUserFacingGate): boolean {
    return gate !== 'HOLD';
}

// ─── Unattended roundup composing ───────────────────────────────────

/** Mirrors the `ChangelogRoundupCadence` DB enum. */
export type ChangelogCadence = 'WEEKLY' | 'FORTNIGHTLY' | 'SPRINT' | 'MANUAL';

export interface ChangelogAutoComposeSettings {
    autoComposeRoundups: boolean;
    roundupCadence: ChangelogCadence;
    sprintLengthDays: number;
    minDeploysToAutoCompose: number;
}

export interface ChangelogAutoComposeState {
    /** When the newest roundup was composed, by anyone. Null when none exists. */
    lastComposedAt: Date | null;
    /** Generated deploys not yet covered by any roundup. */
    candidateDeployCount: number;
    /** The oldest such deploy — the window anchor when no roundup exists yet. */
    oldestCandidateDeployedAt: Date | null;
}

export type ChangelogAutoComposeBlocker =
    | 'automation-off'
    | 'manual-cadence'
    | 'too-few-deploys'
    | 'window-still-open';

export type ChangelogAutoComposeDecision =
    | { compose: true }
    | { compose: false; reason: ChangelogAutoComposeBlocker };

const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;
const DAYS_PER_WEEK = 7;

/** How long one cadence window runs, or null when the team composes by hand. */
export function cadenceLengthDays(
    cadence: ChangelogCadence,
    sprintLengthDays: number,
): number | null {
    switch (cadence) {
        case 'WEEKLY':
            return DAYS_PER_WEEK;
        case 'FORTNIGHTLY':
            return 2 * DAYS_PER_WEEK;
        case 'SPRINT':
            return sprintLengthDays;
        case 'MANUAL':
            return null;
    }
}

/**
 * Whether a roundup should be drafted with nobody clicking Compose.
 *
 * The window is anchored on the newest roundup, so a hand-composed one resets
 * the clock — the automation fills gaps rather than doubling up on a team that
 * is already composing. With no roundup at all, the oldest un-rolled-up deploy
 * anchors it: the first automatic roundup arrives one cadence after deploys
 * started accumulating, not the instant the flag is switched on.
 */
export function decideAutoCompose(
    settings: ChangelogAutoComposeSettings,
    state: ChangelogAutoComposeState,
    now: Date,
): ChangelogAutoComposeDecision {
    if (!settings.autoComposeRoundups) {
        return { compose: false, reason: 'automation-off' };
    }

    const windowDays = cadenceLengthDays(settings.roundupCadence, settings.sprintLengthDays);
    if (windowDays === null) {
        return { compose: false, reason: 'manual-cadence' };
    }

    if (state.candidateDeployCount < settings.minDeploysToAutoCompose) {
        return { compose: false, reason: 'too-few-deploys' };
    }

    const anchor = state.lastComposedAt ?? state.oldestCandidateDeployedAt;
    if (!anchor) {
        // No roundup and no candidate carries a date — with candidates present
        // this cannot happen, so treat it as a window that has not opened.
        return { compose: false, reason: 'window-still-open' };
    }

    if (now.getTime() < anchor.getTime() + windowDays * MS_PER_DAY) {
        return { compose: false, reason: 'window-still-open' };
    }

    return { compose: true };
}

// ─── Unattended roundup sending ─────────────────────────────────────

export interface ChangelogDigestAutoSendSettings {
    requireRoundupApproval: boolean;
    roundupAutoSendDelayMinutes: number;
    quietHoursEnabled: boolean;
    quietHoursStartMinute: number;
    quietHoursEndMinute: number;
    timezone: string;
}

export interface ChangelogDigestAutoSendCandidate {
    approvedAt: Date | null;
    /** Who approved. Only a system approval may be auto-sent. */
    approvedById: string | null;
    /** Delivery rows on any channel. One means a send has already happened. */
    deliveryCount: number;
    itemCount: number;
}

export type ChangelogDigestAutoSendBlocker =
    | 'approval-required'
    | 'not-approved'
    | 'approved-by-hand'
    | 'already-sent'
    | 'nothing-to-say'
    | 'waiting-out-delay'
    | 'quiet-hours';

export type ChangelogDigestAutoSendDecision =
    | { send: true }
    | { send: false; reason: ChangelogDigestAutoSendBlocker };

/**
 * Whether an approved roundup may be sent with nobody watching.
 *
 * Only roundups the system itself approved qualify: a human approval means a
 * human decided to review, and the send is theirs to trigger — mirroring how a
 * deploy entry with any delivery row is left to a human to retry.
 */
export function decideDigestAutoSend(
    settings: ChangelogDigestAutoSendSettings,
    candidate: ChangelogDigestAutoSendCandidate,
    now: Date,
): ChangelogDigestAutoSendDecision {
    if (settings.requireRoundupApproval) {
        return { send: false, reason: 'approval-required' };
    }
    if (!candidate.approvedAt) {
        return { send: false, reason: 'not-approved' };
    }
    if (candidate.approvedById !== CHANGELOG_AUTO_PUBLISH_ACTOR.id) {
        return { send: false, reason: 'approved-by-hand' };
    }
    if (candidate.deliveryCount > 0) {
        return { send: false, reason: 'already-sent' };
    }
    if (candidate.itemCount === 0) {
        return { send: false, reason: 'nothing-to-say' };
    }

    const dueAt =
        candidate.approvedAt.getTime() + settings.roundupAutoSendDelayMinutes * MS_PER_MINUTE;
    if (now.getTime() < dueAt) {
        return { send: false, reason: 'waiting-out-delay' };
    }

    if (
        settings.quietHoursEnabled &&
        isWithinQuietHours(
            minuteOfDayIn(now, settings.timezone),
            settings.quietHoursStartMinute,
            settings.quietHoursEndMinute,
        )
    ) {
        return { send: false, reason: 'quiet-hours' };
    }

    return { send: true };
}
